import { Alert, Anchor, Button, Group, Stack } from "@mantine/core";
import { IconPlus, IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DynamicDashboard } from "../../components/dashboard/DynamicDashboard";
import { EmailVerificationCard } from "../../components/EmailVerificationCard";
import { PageHeader, SectionBlock } from "../../components/ui";
import {
  HR_KYC_COPY,
  hrNeedsIdentityKyc,
  isCompanyAdmin,
  ORG_KYC_COPY,
  orgIsVerified,
} from "../../lib/companyKyc";
import { companyApi, jobsApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";

export function HrDashboardPage() {
  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });
  const jobsQuery = useQuery({
    queryKey: ["hr-jobs", "summary"],
    queryFn: () => jobsApi.list({ page_size: 100 }),
  });

  const jobs = jobsQuery.data?.items ?? [];
  const draftCount = jobs.filter((j) => j.status === "draft").length;
  const latestDraft = jobs
    .filter((j) => j.status === "draft")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.dashboard}
        title={companyQuery.data?.company_name ?? "HR Dashboard"}
        subtitle="Manage job postings and hiring flows for your company."
        actions={
          <Button
            component={Link}
            to="/hr/jobs/new"
            leftSection={<IconPlus size="var(--axio-unit-16)" />}
            variant="filled"
          >
            Post a Job
          </Button>
        }
      />

      {companyQuery.data && !orgIsVerified(companyQuery.data.verification_status) && (
        <Alert color="yellow" title={ORG_KYC_COPY.unverifiedBannerTitle}>
          {isCompanyAdmin(companyQuery.data) ? (
            <>
              {ORG_KYC_COPY.unverifiedClientBody}{" "}
              <Anchor component={Link} to="/hr/kyc" fw="var(--axio-font-weight-semibold)">
                {ORG_KYC_COPY.pageTitleClient}
              </Anchor>
            </>
          ) : (
            ORG_KYC_COPY.restrictedMemberClientBody
          )}
        </Alert>
      )}

      {hrNeedsIdentityKyc(companyQuery.data) && !companyQuery.data?.kyc_bypass && (
        <Alert color="yellow" title={HR_KYC_COPY.bannerTitle}>
          {HR_KYC_COPY.bannerBody}{" "}
          <Anchor component={Link} to="/hr/identity" fw="var(--axio-font-weight-semibold)">
            {HR_KYC_COPY.pageTitle}
          </Anchor>
        </Alert>
      )}

      {companyQuery.data?.kyc_bypass && (
        <Alert color="blue" title={HR_KYC_COPY.bypassTitle}>
          {HR_KYC_COPY.bypassBody}
        </Alert>
      )}

      <EmailVerificationCard />

      <SectionBlock
        title="Ready to hire?"
        description="Post a job, pick a hiring flow template, and recruiters can start submitting candidates."
      >
        {latestDraft && (
          <Anchor component={Link} to={`/hr/jobs/${latestDraft.id}/edit`} size="sm">
            Continue draft: {latestDraft.title}
          </Anchor>
        )}
        <Group gap="sm" mt="xs">
          <Anchor component={Link} to="/hr/jobs" className="quickLinkChip">
            View all jobs
          </Anchor>
          <Anchor component={Link} to="/hr/submissions" className="quickLinkChip">
            Review submissions
          </Anchor>
          <Anchor component={Link} to="/hr/templates" className="quickLinkChip">
            Hiring templates
          </Anchor>
        </Group>
      </SectionBlock>

      <DynamicDashboard />

      {draftCount > 0 && (
        <Alert color="blue" icon={<IconUsers size="var(--axio-unit-16)" />} title="Draft jobs waiting">
          You have {draftCount} draft job{draftCount > 1 ? "s" : ""}. Finish setup and publish when ready.
        </Alert>
      )}
    </Stack>
  );
}
